/**
 * `MAV_MISSION_RESULT`（`MISSION_ACK.type`）→ 中文原因。
 *
 * 这个映射表值得单独成文件，因为它是**唯一**能回答"上传到底成没成"的东西：
 * MAVLink 里没有别的字段表达这件事。把非 0 当成成功（或者只显示"失败"两个字）
 * 会让用户去查链路、查参数、查界面 —— 查遍所有不是原因的地方。
 *
 * 枚举名从生成的协议表取（`@hgc/mavlink`），中文说明是手写的：
 * 名称保证与上游同步，中文保证用户看得懂，两者缺一不可。
 */
import { enumByName } from '@hgc/mavlink';

/** `MAV_MISSION_RESULT` 成功值。**只有它**表示成功 */
export const MAV_MISSION_ACCEPTED = 0;

let resultNames: Map<number, string> | null = null;

/** 结果码 → 上游条目名（`MAV_MISSION_NO_SPACE` 之类）；查不到返回 null */
export function missionResultName(code: number): string | null {
  if (resultNames === null) {
    resultNames = new Map();
    for (const entry of enumByName('MAV_MISSION_RESULT')?.entries ?? []) {
      resultNames.set(entry.value, entry.name);
    }
  }
  return resultNames.get(code) ?? null;
}

/**
 * 结果码的中文说明。
 *
 * 措辞尽量包含"下一步该做什么"：`MAV_MISSION_NO_SPACE` 的用户动作是删掉几个航点，
 * 而 `MAV_MISSION_UNSUPPORTED` 的用户动作是换命令或换机型 —— 两者的处理方式完全不同，
 * 一句"任务被拒绝"会让它们看起来一样。
 */
const RESULT_NOTES: Readonly<Record<number, string>> = Object.freeze({
  1: '传输过程中出错（固件内部错误或链路丢帧），可以重试一次；反复失败请查看飞控日志',
  2: '不支持该坐标框架，任务项应使用全局框架（MAV_FRAME_GLOBAL* / MAV_FRAME_GLOBAL_RELATIVE_ALT）',
  3: '机体不支持该任务类型或其中的命令，请检查命令编号与机型',
  4: '机体存储空间不足，需要减少航点数量',
  5: '机体认为任务项非法（坐标、参数或框架不合法）',
  6: '第 1 个参数（param1）非法',
  7: '第 2 个参数（param2）非法',
  8: '第 3 个参数（param3）非法',
  9: '第 4 个参数（param4）非法',
  10: '纬度（x）非法',
  11: '经度（y）非法',
  12: '高度（z）非法',
  13: '序号错乱：机体收到的任务项顺序与它索要的不一致',
  14: '机体拒绝执行该任务（常见于围栏/备降点未解锁或安全策略限制）',
  15: '机体取消了这次操作（通常是收到了另一条任务传输或解锁状态变化）',
});

/**
 * 把结果码翻译成一句中文原因，例如：
 * `MAV_MISSION_NO_SPACE：机体存储空间不足，需要减少航点数量`
 */
export function missionResultReason(code: number): string {
  const name = missionResultName(code) ?? `未知结果码 ${code}`;
  const note = RESULT_NOTES[code] ?? '机体没有给出可解释的原因，请查看飞控日志';
  return `${name}：${note}`;
}

/** `MISSION_ACK` 的结果码是否表示成功。**只有 0 是成功**，非 0 一律是失败 */
export function isMissionResultAccepted(code: number): boolean {
  return code === MAV_MISSION_ACCEPTED;
}
